import type { InitCache } from "../types/session.ts";

/**
 * Per-database feature flags, delivered as a single opaque JSON blob on
 * InitCache (`cache.DbFeatureFlags`) and configured per database by the
 * backend. These replace what used to be hard-coded customer-id / endpoint-id
 * lists in the client.
 *
 * Per-DB by design: each tenant DB sets only the flags it needs. Absent row →
 * every flag undefined (falsy) → feature hidden. Extend by adding keys here +
 * to the DB JSON; no server change needed (the blob is passed through).
 */
export interface DbFeatureFlags {
  /** Access module: show the Rental price block in the product editor. */
  ProductShowRental?: boolean;
  /** Access module: show the Reserve price block in the product editor. */
  ProductShowReserve?: boolean;
  /** Access module: show the Eat-Here price block in the product editor. */
  ProductShowEatHere?: boolean;
  /** Show the Advanced Tax (AdvTax, PriceType 11-15) price columns on the price
   *  grid. Pair with the server `Script_Flags` 'AdvTax' on the same DB. */
  ProductShowAdvTax?: boolean;
  /** Hide the Reserve "late tax rate" field. */
  ProductHideReserveLateTaxrate?: boolean;
  /** Mark the product editor's core fields as required (was endpoints 138/11). */
  ProductRequiredFields?: boolean;
  /** Edit-Cart: enable TremaSport-specific behaviour (was CustomerID 94). */
  EditCartTremaSport?: boolean;
  /** Create-Operator: show the Technical Fee field (was endpoints 101/114). */
  OperatorTechFee?: boolean;
  /** Edit-Kiosk: show the "Open VNC" action that single-sign-ons into the
   *  InstantRelay VNC repeater. Off until a DB's kiosks are migrated from the
   *  old site to the relay. */
  KioskRelayVnc?: boolean;
  /** Scheduled Export: allow choosing/changing the IANA schedule timezone
   *  (DST-aware firing). Soft-deploy gate — when off the picker is hidden for
   *  new schedules and shown read-only for ones that already have a zone.
   *  Intended to be removed once the feature is validated. */
  ScheduleTimeZoneEdit?: boolean;
  /** Scheduled Export: allow the "Specific weekdays" interval (any combination
   *  of days). Soft-deploy gate — when off the interval option is hidden for
   *  new schedules and the day checkboxes are read-only for ones already on it.
   *  The backend worker handles weekdays rows regardless; this only gates
   *  creation. Intended to be removed once the feature is validated. */
  ScheduleWeekdaysEdit?: boolean;
  /** Show the endpoint's paid-licence counter (used / total kiosk licences) in the top
   *  bar. Hidden regardless of this flag when the partner has no S_Licensed_Kiosks rows
   *  — an endpoint with no licence plan has nothing to count. */
  HeaderShowLicenses?: boolean;
  /** A group's Global database: false turns its group dataset off. On by default (absent
   *  = on): DatasetSync syncs it, the server answers Issa there with the "group" model,
   *  and the chat offers the Analyst (isAnalystEndpoint). False: the Guide only. */
  IssaGroupDataset?: boolean;
  /** Scheduled Export: offer IANA zone ids (e.g. Europe/Stockholm) in the report
   *  "Timezone" (output) picker instead of the legacy Windows zone names. Old
   *  stored values still load and display either way. Soft-deploy gate — the
   *  backend already resolves both via TimeZoneConverter. Remove once validated. */
  ReportTimezoneIana?: boolean;
  /** Package Deal: show the "Mix" (free mix) checkbox on deal groups — the group's item
   *  count may be any mix of its products. Soft-deploy gate: kiosks built before r59019 ignore
   *  the flag and read the group as N of ONE product, so enable once this DB's kiosks run a
   *  build with it. While off, a group that already has it keeps it (hidden, not stripped),
   *  and Issa's free-mix suggestions are written as N identical groups of one, which every
   *  kiosk prices the same. Remove once the fleet is updated. */
  PackageDealFreeMix?: boolean;
  /** Current Rentals "Operations": hide the plain Return button (which only stamps the
   *  row's return date) and relabel "Open Refund Menu" as "Return Operations", so rentals
   *  are returned with "Set Reservation as Returned" in Transaction Operations. Also shows
   *  that action without ProductShowReserve. Was CustomerIDs 149 (NortecEU) / 122 (AccessIRS). */
  RentalReturnViaOperations?: boolean;
}

/**
 * Parse `cache.DbFeatureFlags` (raw JSON) into typed flags. Returns `{}` when
 * the blob is missing or malformed, so callers get `undefined` (falsy) per flag.
 */
export function getDbFeatureFlags(
  cache: Pick<InitCache, "DbFeatureFlags"> | null | undefined,
): DbFeatureFlags {
  const raw = cache?.DbFeatureFlags;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object" ? parsed : {}) as DbFeatureFlags;
  } catch {
    return {};
  }
}
